import _ from 'lodash';
import type { ThunkAction } from 'types/redux';
import constants from '#packages/constants';
import * as coreBi from '#packages/coreBi';
import * as platformEvents from 'platformEvents';
import coreUtilsLib from 'coreUtilsLib';
import * as bi from '../../bi/bi';
import * as highlightsActions from '../../highlights/highlightsActions';
import * as inlinePopupActions from '../../inlinePopup/inlinePopupActions';
import * as actionTypes from './hiddenItemsActionTypes';
import * as selectors from './hiddenItemsSelectors';
import * as selectionSelectors from '../../selection/selectionSelectors';
import * as panelsSelectors from '../../panels/panelsSelectors';
import * as pagesSelectors from '../../pages/pagesSelectors';
import * as componentLocatorActions from '../../componentLocator/componentLocatorActions';
import type { CompRef } from '@wix/document-services-types';
import type { EditorAPI } from '#packages/editorAPI';
import experiment from 'experiment';
import type { EditorState, MobileState } from '#packages/stateManagement';
import type {
  DSAction,
  DSRead,
  DocumentServicesObject,
} from 'types/documentServices';
import type { HiddenItemData } from '#packages/mobileEditor';

const isRedesignExperimentDisabled =
  experiment.getValue('se_leftPanelHiddenMobileElementsRedesign') !== 'B' &&
  experiment.getValue('se_leftPanelHiddenMobileElementsRedesign') !== 'C';
let hiddenItemsUtils: AnyFixMe; // because of require.js circular dependencies issues. remove when moving to Webpack

const { event } = bi.actions;
const isMasterPage = (pageId: string) => pageId === 'masterPage';

const markHoveredComponent =
  (compId: string, pageId: string): ThunkAction =>
  (dispatch, getState) => {
    const state = getState();
    const masterPageHiddenComponents =
      selectors.getMasterPageHiddenComponents(state);
    const currentPageHiddenComponents =
      selectors.getCurrentPageHiddenComponents(state);

    const hiddenComps = isMasterPage(pageId)
      ? masterPageHiddenComponents
      : currentPageHiddenComponents;
    const hiddenComp = hiddenComps.find((comp) => comp.compId === compId);

    dispatch({
      type: actionTypes.SET_HIDDEN_ITEMS_HOVERED_COMPONENT,
      hoveredComponent: {
        hiddenComp,
        pageId,
        isInteraction: false,
      },
    });
  };

const refreshMobileHiddenComponents =
  (): ThunkAction =>
  (dispatch, getState, { editorAPI }) => {
    if (!hiddenItemsUtils) {
      return;
    }
    const state = getState();
    const focusedPageId = pagesSelectors.getFocusedPageId(state);
    const hoveredComponent = selectors.getHoveredComponent(state);
    const masterPageHiddenComponents = hiddenItemsUtils.getHiddenCompsForPage(
      editorAPI,
      'masterPage',
    );
    const currentPageHiddenComponents = hiddenItemsUtils.getHiddenCompsForPage(
      editorAPI,
      focusedPageId,
    );
    const hiddenItemSorter = ['desktopLayout.y', 'desktopLayout.x', 'compId'];

    const hiddenComponentsOfMasterPageOrCurrentPage = isMasterPage(
      hoveredComponent.pageId,
    )
      ? masterPageHiddenComponents
      : currentPageHiddenComponents;
    if (
      !_.isEmpty(hoveredComponent) &&
      !hoveredComponent.isInteraction &&
      !hiddenComponentsOfMasterPageOrCurrentPage.find(
        (
          comp:
            | HiddenItemData
            | {
                compId: any;
                compType: any;
                label: string;
                iconUrl: string;
                svgName: string;
                css: object;
              },
        ) => comp === hoveredComponent.hiddenComp,
      )
    ) {
      hiddenComponentsOfMasterPageOrCurrentPage.push(
        hoveredComponent.hiddenComp,
      );
    }

    dispatch({
      type: actionTypes.SET_HIDDEN_ITEMS_MASTER_PAGE_COMPS,
      hiddenComponents: _.sortBy(masterPageHiddenComponents, hiddenItemSorter),
    });

    dispatch({
      type: actionTypes.SET_HIDDEN_ITEMS_CURRENT_PAGE_COMPS,
      hiddenComponents: _.sortBy(currentPageHiddenComponents, hiddenItemSorter),
    });
  };

const isMobileHiddenItemsPanelOpen = (state: EditorState) =>
  panelsSelectors.isMobileHiddenItemsPanelOpen(state);

const refreshMobileHiddenComponentsIfPanelIsOpen =
  (): ThunkAction => (dispatch, getState) => {
    if (isMobileHiddenItemsPanelOpen(getState())) {
      dispatch(refreshMobileHiddenComponents());
    }
  };

const hideHiddenItemsCounter =
  (): ThunkAction =>
  (dispatch, getState, { editorAPI }) => {
    editorAPI.mobile.hideHiddenItemsCounter();
  };

const initHiddenItemsPanel =
  (_hiddenItemsUtils: typeof hiddenItemsUtils): ThunkAction =>
  (dispatch, getState, { editorAPI }) => {
    hiddenItemsUtils =
      typeof hiddenItemsUtils !== 'undefined'
        ? hiddenItemsUtils
        : _hiddenItemsUtils;
    dispatch(refreshMobileHiddenComponents());
    if (isRedesignExperimentDisabled) {
      dispatch(hideHiddenItemsCounter());
    }
    editorAPI.selection.deselectComponents();
  };

const clearHiddenItems = (): ThunkAction => (dispatch, getState) => {
  const hoveredComponent = selectors.getHoveredComponent(getState());
  const compId = hoveredComponent?.hiddenComp?.compId;
  const pageId = hoveredComponent?.pageId;
  if (compId && pageId) {
    dispatch(stopPreviewReAddComponent());
  }

  dispatch(highlightsActions.clearHighlights());
  dispatch(clearHoveredComponent());
};

const SCROLL_MARGIN = 50;

const highlightDefs = (
  compRef: CompRef,
  layout: DocumentServicesObject['components']['layout'],
  dsRead: DSRead,
) => {
  const repeatedItems =
    dsRead.deprecatedOldBadPerformanceApis.components.getRepeatedComponents(
      compRef,
    );
  const repeatedItemsDefs = repeatedItems.map((itemRef: CompRef) => ({
    compRef: itemRef,
    layout: dsRead.components.layout.getRelativeToScreen(itemRef),
    type: constants.UI.HIGHLIGHTS.TYPES.OVERLAY,
  }));

  return repeatedItemsDefs
    ? repeatedItemsDefs
    : [
        {
          compRef,
          layout,
          type: constants.UI.HIGHLIGHTS.TYPES.OVERLAY,
        },
      ];
};

const highlightComp =
  (
    compRef: CompRef,
    layout: DocumentServicesObject['components']['layout'],
  ): ThunkAction =>
  (dispatch, getState, { dsRead }) => {
    if (!isMobileHiddenItemsPanelOpen(getState())) {
      return;
    }

    const highlightsDefs = highlightDefs(compRef, layout, dsRead);
    dispatch(highlightsActions.setHighlights(highlightsDefs));
  };

const clearHoveredComponent = () => ({
  type: actionTypes.SET_HIDDEN_ITEMS_HOVERED_COMPONENT,
  hoveredComponent: {},
});

const addComponentToStageAndScrollToIt =
  (
    compId: string,
    pageId: string,
    scrollCallback: () => void,
    showCallback = _.noop,
  ): ThunkAction =>
  async (
    dispatch,
    getState,
    { dsActions, editorAPI }: { editorAPI: EditorAPI; dsActions: DSAction },
  ) => {
    if (!isMobileHiddenItemsPanelOpen(getState())) {
      return;
    }

    const canStillPreviewComp = (compRef: CompRef) =>
      compRef && editorAPI.components.is.exist(compRef);

    dsActions.mobile.hiddenComponents.show(compId, pageId);
    await editorAPI.waitForChangesAppliedAsync();

    const mobileCompRef = editorAPI.components.get.byId(compId);
    if (mobileCompRef) {
      const baseTransformations =
        editorAPI.components.transformations.get(mobileCompRef);
      if (baseTransformations?.hidden === true) {
        const mobileVariant = editorAPI.variants.mobile.get();
        if (mobileVariant) {
          const compRefWithMobileVariant =
            editorAPI.components.variants.getPointer(
              mobileCompRef,
              mobileVariant,
            );
          editorAPI.components.transformations.update(
            compRefWithMobileVariant,
            { hidden: false },
          );
          await editorAPI.waitForChangesAppliedAsync();
        }
      }
    }

    callbacksAndScrollToComp(
      compId,
      canStillPreviewComp,
      editorAPI,
      scrollCallback,
      showCallback,
    );

    editorAPI.components.hooks.componentAddedToStage.fire({
      origin: 'unhide_preview',
      compRef: editorAPI.components.get.byId(compId),
      type: 'click',
    });
  };

const callbacksAndScrollToComp = (
  compId: string,
  canStillPreviewComp: (compRef: CompRef) => boolean,
  editorAPI: EditorAPI,
  scrollCallback: () => void,
  showCallback = _.noop,
) => {
  const compRef = editorAPI.components.get.byId(compId);
  if (!canStillPreviewComp(compRef)) {
    return;
  }
  showCallback(compRef);

  if (scrollCallback) {
    const compLayout = editorAPI.components.layout.getRelativeToScreen(compRef);
    const targetY = compLayout.y - SCROLL_MARGIN;

    const scrollDuration = coreUtilsLib.scrollUtils.calcScrollDuration(
      editorAPI.site.getScroll().y,
      targetY,
      true,
    );
    editorAPI.scroll.animateScrollTo({ y: targetY }, scrollDuration, () => {
      if (canStillPreviewComp(compRef)) {
        scrollCallback();
      }
    });
  }

  editorAPI.dsActions.platform.notifyAppsOnCustomEvent(
    platformEvents.factory.showMobileElement({ compRef }),
  );
};
const setIsAddingComponentInProgress =
  (bool: boolean): ThunkAction =>
  (dispatch) => {
    dispatch({
      type: actionTypes.SET_IS_ADDING_COMPONENT_IN_PROGRESS,
      isAddingHiddenItemInProgress: bool,
    });
  };

const setPreviewHiddenCompState =
  (
    preview_state: MobileState['hiddenItems']['previewHiddenCompState'],
  ): ThunkAction =>
  (dispatch) => {
    dispatch({
      type: actionTypes.SET_PREVIEW_HIDDEN_COMP_STATE,
      preview_state,
    });
  };

const reAddComponent =
  (
    compId: string,
    pageId: string,
    compType: string,
    sectionType: string,
  ): ThunkAction =>
  (dispatch, getState, { editorAPI }) => {
    dispatch(setIsAddingComponentInProgress(true));
    const actionType = isMasterPage(pageId)
      ? actionTypes.REMOVE_HIDDEN_ITEM_FROM_MASTER_PAGE_LIST
      : actionTypes.REMOVE_HIDDEN_ITEM_FROM_CURRENT_PAGE_LIST;

    window.setTimeout(() => {
      dispatch(highlightsActions.clearHighlights());
      dispatch(clearHoveredComponent());
      dispatch({ type: actionType, compId });

      dispatch(
        addComponentToStageAndScrollToIt(compId, pageId, () => {
          const compRef = editorAPI.components.get.byId(compId, pageId);
          if (editorAPI.components.is.exist(compRef)) {
            editorAPI.selection.selectComponentByCompRef(compRef);
          }

          const focusedContainer =
            selectionSelectors.getFocusedContainer(getState());
          if (editorAPI.components.is.inlinePopup(focusedContainer)) {
            dispatch(inlinePopupActions.open(focusedContainer));
          }

          dispatch(setIsAddingComponentInProgress(false));
        }),
      );

      editorAPI.history.add('unhide mobile component');
    }, 1000);

    dispatch(sendReAddBiEvent(compId, pageId, compType, sectionType));
  };

const stopPreviewReAddComponent =
  (): ThunkAction =>
  async (
    dispatch,
    getState,
    { dsActions, editorAPI }: { editorAPI: EditorAPI; dsActions: DSAction },
  ) => {
    if (selectors.getPreviewHiddenCompState(getState()) === 'in_progress') {
      dispatch(setPreviewHiddenCompState('terminating'));

      await editorAPI.waitForChangesAppliedAsync();
    }

    if (selectors.getPreviewHiddenCompState(getState()) === 'no_preview') {
      return;
    }

    const isAddingHiddenItemInProgress =
      selectors.getIsAddingHiddenItemInProgress(getState());
    if (isAddingHiddenItemInProgress) {
      return;
    }

    dispatch(highlightsActions.clearHighlights());
    dispatch(clearHoveredComponent());
    dispatch(setPreviewHiddenCompState('no_preview'));
    dsActions.history.applyLatestSnapshot();
  };

const previewReAddComponent =
  (compId: string, pageId: string): ThunkAction =>
  (dispatch, getState, { editorAPI }: { editorAPI: EditorAPI }) => {
    const isAddingHiddenItemInProgress =
      selectors.getIsAddingHiddenItemInProgress(getState());
    const isPreviewHiddenItemInProgress =
      selectors.getPreviewHiddenCompState(getState()) === 'in_progress';
    if (isAddingHiddenItemInProgress || isPreviewHiddenItemInProgress) {
      return;
    }
    dispatch(setPreviewHiddenCompState('in_progress'));

    dispatch(markHoveredComponent(compId, pageId));

    editorAPI.selection.deselectComponents();
    dispatch(highlightsActions.clearHighlights());

    dispatch(
      addComponentToStageAndScrollToIt(
        compId,
        pageId,
        null,
        (compRef: CompRef) => {
          editorAPI.store.dispatch(
            componentLocatorActions.locateComponentAndHighlight(
              compRef,
              true,
              constants.UI.HIGHLIGHTS.TYPES.NORMAL_WITH_OVERLAY,
            ),
          );
        },
      ),
    );
  };

const sendReAddBiEvent =
  (
    compId: string,
    pageId: string,
    compType: string,
    sectionType: string,
  ): ThunkAction =>
  (dispatch, getState, { dsRead, editorAPI }) => {
    const params: {
      current_page_name: string;
      component_type: string;
      section: string;
      component_id: string;
      app_id?: string;
      app_site_id?: string;
      instance_id?: string;
    } = {
      current_page_name: pageId,
      component_type: compType,
      section: sectionType,
      component_id: compId,
    };
    if (editorAPI.isTpa(compType)) {
      const compRef = editorAPI.components.get.byId(
        compId,
        pageId,
        editorAPI.dsRead.viewMode.VIEW_MODES.DESKTOP,
      );
      const compData = editorAPI.components.data.get(compRef);
      const appData = dsRead.tpa.app.getData(compData.applicationId);
      params.app_id = appData.appDefinitionId;
      params.app_site_id = appData.instanceId;
      params.instance_id = compId;
    }
    dispatch(
      event(
        coreBi.events.mobileEditor.hiddenElements.HIDDEN_ELEMENTS_SHOW,
        params,
      ),
    );
  };

export {
  initHiddenItemsPanel,
  isMobileHiddenItemsPanelOpen,
  clearHiddenItems,
  clearHoveredComponent,
  callbacksAndScrollToComp,
  stopPreviewReAddComponent,
  previewReAddComponent,
  refreshMobileHiddenComponentsIfPanelIsOpen,
  reAddComponent,
  hideHiddenItemsCounter,
  sendReAddBiEvent,
  highlightComp,
};
